import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { GraduationCap, AlertTriangle, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureToggles } from '@/hooks/useFeatureToggles';
import { differenceInDays, format } from 'date-fns';
import { mockOverdueTrainings } from '@/data/mockData';

interface OverdueTrainingsCardProps {
  onViewAll?: () => void;
}

interface UserRole {
  user_id: string;
  role: string;
}

interface OverdueItem {
  assignmentId?: string;
  staffName: string;
  staffId: string;
  trainingTitle: string;
  materialId: string;
  dueDate: string;
  daysOverdue: number;
}

// Role-based visibility matrix
const getVisibleStaffIds = (
  viewerRole: string,
  viewerUserId: string,
  allUserRoles: UserRole[]
): string[] | null => {
  switch (viewerRole) {
    case 'residential_aide':
    case 'case_manager':
      return [viewerUserId];
    case 'supervisor':
      return [
        viewerUserId, 
        ...allUserRoles
          .filter(ur => ur.role === 'residential_aide')
          .map(ur => ur.user_id)
      ];
    case 'manager':
      return [
        viewerUserId, 
        ...allUserRoles
          .filter(ur => ['supervisor', 'residential_aide'].includes(ur.role))
          .map(ur => ur.user_id)
      ];
    case 'program_director':
    case 'super_admin':
      return null;
    default:
      return [viewerUserId];
  }
};

export const OverdueTrainingsCard: React.FC<OverdueTrainingsCardProps> = ({ onViewAll }) => {
  const { user, userRole, isDemoMode } = useAuth();
  const { isFeatureEnabled } = useFeatureToggles();
  
  const isEnabled = isFeatureEnabled('dashboard_overdue_trainings', userRole);
  const isPersonalView = userRole === 'residential_aide' || userRole === 'case_manager';

  // Fetch all user roles for visibility matrix
  const { data: allUserRoles = [] } = useQuery({
    queryKey: ['all-user-roles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_roles')
        .select('user_id, role');
      if (error) throw error;
      return data as UserRole[];
    },
    enabled: isEnabled && !isPersonalView && !isDemoMode
  });

  // Fetch all profiles for staff names
  const { data: profiles = [] } = useQuery({
    queryKey: ['profiles-for-training'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name');
      if (error) throw error;
      return data;
    },
    enabled: isEnabled && !isDemoMode
  });

  // Fetch training assignments
  const { data: assignments = [] } = useQuery({
    queryKey: ['training-assignments-overdue'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_assignments')
        .select('id, material_id, assigned_to_user_id, assigned_to_role, due_date')
        .not('due_date', 'is', null);
      if (error) throw error;
      return data;
    },
    enabled: isEnabled && !isDemoMode
  });

  // Fetch training materials for titles
  const { data: materials = [] } = useQuery({
    queryKey: ['training-materials-titles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_materials')
        .select('id, title')
        .eq('is_active', true);
      if (error) throw error;
      return data;
    },
    enabled: isEnabled && !isDemoMode
  });

  // Fetch all completions
  const { data: completions = [] } = useQuery({
    queryKey: ['training-completions-all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_completions')
        .select('material_id, user_id');
      if (error) throw error;
      return data;
    },
    enabled: isEnabled && !isDemoMode
  });

  // Calculate overdue items with role filtering
  const overdueItems: OverdueItem[] = React.useMemo(() => {
    if (!isEnabled) return [];

    // Demo mode: use mock data directly
    if (isDemoMode) {
      return mockOverdueTrainings.map(item => ({
        ...item,
        daysOverdue: differenceInDays(new Date(), new Date(item.dueDate)),
      })).sort((a, b) => b.daysOverdue - a.daysOverdue).slice(0, 5);
    }

    if (!user?.id) return [];

    const today = new Date();
    const visibleStaffIds = getVisibleStaffIds(userRole || '', user.id, allUserRoles);
    
    const items: OverdueItem[] = [];

    assignments.forEach(assignment => {
      if (!assignment.due_date) return;
      
      const dueDate = new Date(assignment.due_date);
      if (dueDate >= today) return;
      
      const daysOverdue = differenceInDays(today, dueDate);
      
      if (assignment.assigned_to_user_id) {
        if (visibleStaffIds !== null && !visibleStaffIds.includes(assignment.assigned_to_user_id)) {
          return;
        }
        
        const isCompleted = completions.some(
          c => c.material_id === assignment.material_id && c.user_id === assignment.assigned_to_user_id
        );
        if (isCompleted) return;
        
        const staff = profiles.find(p => p.id === assignment.assigned_to_user_id);
        const material = materials.find(m => m.id === assignment.material_id);
        
        items.push({
          assignmentId: assignment.id,
          staffName: staff?.full_name || 'Unknown Staff',
          staffId: assignment.assigned_to_user_id,
          trainingTitle: material?.title || 'Unknown Training',
          materialId: assignment.material_id,
          dueDate: assignment.due_date,
          daysOverdue
        });
      }
      
      if (assignment.assigned_to_role) {
        const usersWithRole = allUserRoles
          .filter(ur => ur.role === assignment.assigned_to_role)
          .map(ur => ur.user_id);
        
        usersWithRole.forEach(userId => {
          if (visibleStaffIds !== null && !visibleStaffIds.includes(userId)) {
            return;
          }
          
          const isCompleted = completions.some(
            c => c.material_id === assignment.material_id && c.user_id === userId
          );
          if (isCompleted) return;
          
          const staff = profiles.find(p => p.id === userId);
          const material = materials.find(m => m.id === assignment.material_id);
          
          if (items.some(i => i.staffId === userId && i.materialId === assignment.material_id)) {
            return;
          }
          
          items.push({
            assignmentId: assignment.id,
            staffName: staff?.full_name || 'Unknown Staff',
            staffId: userId,
            trainingTitle: material?.title || 'Unknown Training',
            materialId: assignment.material_id,
            dueDate: assignment.due_date,
            daysOverdue
          });
        });
      }
    });

    return items.sort((a, b) => b.daysOverdue - a.daysOverdue).slice(0, 5);
  }, [user?.id, userRole, isEnabled, isDemoMode, assignments, completions, profiles, materials, allUserRoles]);

  if (!isEnabled) return null;

  const title = isPersonalView ? 'Your Overdue Trainings' : 'Overdue Trainings';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <GraduationCap className="h-4 w-4" />
            {title}
            {overdueItems.length > 0 && (
              <Badge variant="destructive" className="ml-2">
                {overdueItems.length}
              </Badge>
            )}
          </CardTitle>
          {onViewAll && (
            <button 
              onClick={onViewAll}
              className="text-xs text-primary hover:underline flex items-center gap-1"
            >
              View All
              <ChevronRight className="h-3 w-3" />
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {overdueItems.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            <GraduationCap className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No overdue trainings</p>
          </div>
        ) : isPersonalView ? (
          <div className="space-y-2">
            {overdueItems.map(item => (
              <div 
                key={`${item.staffId}-${item.materialId}`}
                className="flex items-center justify-between p-2 rounded-lg bg-destructive/5 border border-destructive/20"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  <span className="text-sm font-medium">{item.trainingTitle}</span>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(item.dueDate), 'MMM d')}
                  </p>
                  <p className="text-xs text-destructive font-medium">
                    {item.daysOverdue} {item.daysOverdue === 1 ? 'day' : 'days'} overdue
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground font-medium pb-1 border-b">
              <span>Staff</span>
              <span>Training</span>
              <span className="text-right">Overdue</span>
            </div>
            {overdueItems.map(item => (
              <div 
                key={`${item.staffId}-${item.materialId}`}
                className="grid grid-cols-3 gap-2 text-sm p-2 rounded-lg hover:bg-muted/50"
              >
                <span className="font-medium truncate">{item.staffName}</span>
                <span className="truncate text-muted-foreground">{item.trainingTitle}</span>
                <span className="text-right text-destructive font-medium">
                  {item.daysOverdue} {item.daysOverdue === 1 ? 'day' : 'days'}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};